import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { meetingsApi } from '@/services/meetings';
import { Meeting } from '@/types';
import { NotesCapture } from '@/components/meeting/NotesCapture';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const AddNotePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchMeeting();
    }
  }, [id]);

  const fetchMeeting = async () => {
    try {
      setLoading(true);
      const response = await meetingsApi.getMeetingById(parseInt(id!));
      setMeeting(response.meeting);
    } catch (error: any) {
      toast.error('Failed to load meeting details');
      console.error(error);
      navigate('/history');
    } finally {
      setLoading(false);
    }
  };

  const handleNotesComplete = () => {
    toast.success('Notes added successfully!');
    navigate(`/meeting/${id}`, { state: { refreshTimestamp: Date.now() } });
  };

  const handleNoteAdded = () => {
    // Automatically return to meeting detail page after adding a note
    navigate(`/meeting/${id}`, { state: { refreshTimestamp: Date.now() } });
  };

  const handleCancel = () => {
    navigate(`/meeting/${id}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen space-y-4">
        <p className="text-muted-foreground">Meeting not found</p>
        <Button onClick={() => navigate('/history')}>Back to History</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="space-y-1 min-w-0 flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Meeting
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold text-foreground truncate">Add Notes</h1>
          <p className="text-xs sm:text-sm text-muted-foreground truncate">
            Add notes to meeting from {new Date(meeting.meeting_date).toLocaleDateString('pl-PL')}
          </p>
        </div>
        <Button variant="outline" onClick={handleCancel} className="hidden sm:flex shrink-0">
          Cancel
        </Button>
      </div>

      <NotesCapture
        meetingId={meeting.meeting_id}
        meetingDate={meeting.meeting_date}
        meetingTime={meeting.meeting_time}
        location={meeting.location}
        onFinish={handleNotesComplete}
        onNoteAdded={handleNoteAdded}
      />
    </div>
  );
};

export default AddNotePage;
